import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { aiChatSearch, getJobStatus, getJobResults, saveChatSession, getLatestChat } from '../utils/api';

const ChatSearch = ({ onSearchStart, onSearchComplete, onSearchError, activeSessionId, selectedSessionData, onNewChat: parentNewChat, onSessionSaved, newChatTrigger }) => {
  const { profile } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [searchIntent, setSearchIntent] = useState('');
  const [hasAIAnalysis, setHasAIAnalysis] = useState(false);
  const [searchStrategy, setSearchStrategy] = useState('');
  const [sessionTitle, setSessionTitle] = useState('New Search');
  const [isRestoring, setIsRestoring] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');

  const navigate = useNavigate();

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const pollingRef = useRef(null);
  const completedRef = useRef(false);
  const messagesRef = useRef(messages);
  const saveDataRef = useRef({ messages, sessionId, sessionTitle, currentJobId });
  const hasInitializedRef = useRef(false);

  const storageKey = profile?.id ? `leadgen_chat_${profile.id}` : null;

  useEffect(() => {
    messagesRef.current = messages;
    saveDataRef.current = { messages, sessionId, sessionTitle, currentJobId };
  }, [messages, sessionId, sessionTitle, currentJobId]);

  useEffect(() => {
    const restoreSession = async () => {
      // Prevent duplicate restore on re-mount or tab switch
      if (hasInitializedRef.current) return;

      // Prevent restore from overwriting if we already have selectedSessionData
      if (selectedSessionData) {
        setIsRestoring(false);
        hasInitializedRef.current = true;
        return;
      }

      if (!profile?.id) {
        setIsRestoring(false);
        hasInitializedRef.current = true;
        return;
      }

      try {
        if (storageKey) {
          const localData = localStorage.getItem(storageKey);
          if (localData) {
            const parsed = JSON.parse(localData);
            if (parsed.messages && parsed.messages.length > 0) {
              const restoredMessages = parsed.messages.map(msg => ({
                ...msg,
                timestamp: new Date(msg.timestamp)
              }));
              setMessages(restoredMessages);
              setSessionId(parsed.sessionId || null);
              setSessionTitle(parsed.sessionTitle || 'New Search');
              setCurrentJobId(parsed.currentJobId || null);
              setIsRestoring(false);
              hasInitializedRef.current = true;
              return;
            }
          }
        }

        const latest = await getLatestChat();
        if (latest && latest.session !== null && latest.id) {
          const restoredMessages = (latest.messages || []).map(msg => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }));
          setMessages(restoredMessages);
          setSessionId(latest.id);
          setSessionTitle(latest.title || 'New Search');
          setCurrentJobId(latest.job_id || null);

          // Cache to localStorage for instant restore next time
          if (storageKey) {
            try {
              localStorage.setItem(storageKey, JSON.stringify({
                messages: latest.messages,
                sessionId: latest.id,
                sessionTitle: latest.title,
                currentJobId: latest.job_id
              }));
            } catch (e) {}
          }
        }
      } catch (err) {
        console.error('Error restoring session:', err);
      } finally {
        setIsRestoring(false);
        hasInitializedRef.current = true;
      }
    };

    restoreSession();
  }, [profile?.id, storageKey]);

  // Handle sidebar session selection
  useEffect(() => {
    if (selectedSessionData) {
      if (pollingRef.current) clearTimeout(pollingRef.current);
      setIsSearching(false);
      
      const restoredMessages = (selectedSessionData.messages || []).map(msg => ({
        ...msg,
        timestamp: new Date(msg.timestamp)
      }));
      
      setMessages(restoredMessages);
      setSessionId(selectedSessionData.id);
      setSessionTitle(selectedSessionData.title || 'New Search');
      setCurrentJobId(selectedSessionData.job_id || null);
      
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify({
            messages: restoredMessages,
            sessionId: selectedSessionData.id,
            sessionTitle: selectedSessionData.title || 'New Search',
            currentJobId: selectedSessionData.job_id || null
          }));
        } catch(e) {}
      }
    }
  }, [selectedSessionData, storageKey]);

  // Handle New Chat triggered from sidebar via Layout
  useEffect(() => {
    if (newChatTrigger > 0) {
      if (pollingRef.current) clearTimeout(pollingRef.current);
      setMessages([]);
      setSessionId(null);
      setSessionTitle('New Search');
      setCurrentJobId(null);
      setIsSearching(false);
      setIsLoading(false);
      if (storageKey) localStorage.removeItem(storageKey);
    }
  }, [newChatTrigger, storageKey]);

  useEffect(() => {
    if (!storageKey || messages.length === 0) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify({
        messages,
        sessionId,
        sessionTitle,
        currentJobId
      }));
    } catch (e) {
      console.error('LocalStorage save error:', e);
    }
  }, [messages, sessionId, sessionTitle, currentJobId, storageKey]);

  const saveToSupabase = async () => {
    const { messages: curMessages, sessionId: curId, sessionTitle: curTitle, currentJobId: curJobId } = saveDataRef.current;
    if (curMessages.length === 0) return;
    try {
      const payload = {
        session_id: curId,
        title: curTitle,
        messages: curMessages,
        search_params: null,
        job_id: curJobId || undefined
      };
      const response = await saveChatSession(payload);
      if (response && response.id && !curId) {
        setSessionId(response.id);
      }
      if (onSessionSaved) onSessionSaved();
    } catch (err) {
      console.error('Error saving to Supabase:', err);
    }
  };

   useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading, isSearching]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
    };
  }, []);

  const addMessage = (type, text, data = null) => {
    setMessages(prev => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        type,
        text,
        data,
        timestamp: new Date()
      }
    ]);
  };

  const pollForResults = (jobId, requestedCount = 20) => {
    setIsSearching(true);
    // Kill any existing timer
    if (pollingRef.current) { clearTimeout(pollingRef.current); pollingRef.current = null; }
    completedRef.current = false;
    
    const maxPolls = requestedCount > 200 ? 288 : requestedCount > 100 ? 192 : requestedCount > 50 ? 144 : requestedCount > 20 ? 96 : 48;
    let pollCount = 0;
    
    const poll = async () => {
      // Guard: if already completed, stop
      if (completedRef.current) return;
      
      pollCount++;
      
      // Timeout check
      if (pollCount > maxPolls) {
        pollingRef.current = null;
        setIsSearching(false);
        addMessage('ai', 'Search is taking longer than expected. Your search might still be running — check your search history in a few minutes, or try searching with fewer leads.');
        return;
      }
      
      try {
        const status = await getJobStatus(jobId);
        
        if (status.status === 'completed') {
          // DONE — set completed flag FIRST
          completedRef.current = true;
          pollingRef.current = null;
          
          try {
            const results = await getJobResults(jobId, 0, 1000);
            setIsSearching(false);
            addMessage('ai', `Found ${status.total_leads || results.leads.length} leads! Click below to view full results with email finder, filters & export.`, {
              type: 'results',
              leads: results.leads,
              jobId: jobId,
              totalLeads: status.total_leads || results.leads.length
            });
            setTimeout(() => saveToSupabase(), 1000);
          } catch (resultsError) {
            setIsSearching(false);
            addMessage('ai', 'Sorry, an error occurred while fetching the generated results.');
            setTimeout(() => saveToSupabase(), 500);
          }
          return; // STOP — don't schedule next poll
          
        } else if (status.status === 'failed' || status.status === 'error') {
          completedRef.current = true;
          pollingRef.current = null;
          setIsSearching(false);
          addMessage('ai', 'Sorry, the search failed: ' + (status.error_message || 'Unknown error'));
          setTimeout(() => saveToSupabase(), 500);
          return; // STOP
        }
      } catch (err) {
        // network hiccup — continue polling
      }
      
      // Schedule next poll (only reaches here if NOT completed/failed)
      pollingRef.current = setTimeout(poll, 2500);
    };
    
    // Start first poll after 2.5 seconds
    pollingRef.current = setTimeout(poll, 2500);
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) return;
      if (completedRef.current) return;
      if (pollingRef.current) return; // polling already running
      // Only restart if we somehow lost the interval
      if (currentJobId) {
        pollForResults(currentJobId);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentJobId]);

  const handleSendMessage = async (overridePrompt = null) => {
    const prompt = (overridePrompt !== null ? overridePrompt : inputValue).trim();
    if (!prompt || isLoading || isSearching) return;
    
    if (profile?.plan === 'free') {
      setUpgradeMessage("Your free trial has ended. Please upgrade to a paid plan to continue finding leads.");
      setShowUpgradeModal(true);
      return;
    }

    setInputValue('');
    if (inputRef.current) {
      inputRef.current.style.height = 'auto'; // Reset height
    }
    
    setIsLoading(true);
    addMessage('user', prompt);
    
    if (sessionTitle === 'New Search') {
      setSessionTitle(prompt.substring(0, 50));
    }
    
    try {
      // Build conversation history from existing messages (last 10 text-only messages)
      const conversationHistory = messages
        .filter(msg => msg.text && typeof msg.text === 'string' && msg.text.trim() && !msg.data)
        .slice(-10)
        .map(msg => ({
          role: msg.type === 'user' ? 'user' : 'assistant',
          content: msg.text
        }));

      const result = await aiChatSearch(prompt, null, conversationHistory.length > 0 ? conversationHistory : null);
      
      if (result.status === 'chat_response') {
        addMessage('ai', result.ai_message);
      } else if (result.status === 'clarification_needed') {
        addMessage('ai', result.ai_message);
      } else if (result.source === 'youtube' && result.status === 'completed') {
        // YouTube results come back instantly — no polling needed
        addMessage('ai', result.ai_message || '🎬 YouTube search completed!');
        setCurrentJobId(result.job_id);
        addMessage('ai', `Found ${result.total_leads || 0} YouTube channel leads!`, {
          type: 'results',
          source: 'youtube',
          leads: result.leads || [],
          jobId: result.job_id,
          totalLeads: result.total_leads || 0
        });
      } else if (result.source === 'platform' && result.status === 'completed') {
        // Platform results come back instantly too
        addMessage('ai', result.ai_message || '🔍 Platform search completed!');
        setCurrentJobId(result.job_id);
        
        // Fetch actual leads from results endpoint
        const jobResults = await getJobResults(result.job_id, 0, 1000);
        const leads = jobResults?.leads || [];
        
        const platforms = result.platforms || [];
        const platformLabel = platforms.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' + ');
        
        addMessage('ai', `Found ${result.total_leads || 0} leads from ${platformLabel}!`, {
          type: 'results',
          source: 'platform',
          platforms: platforms,
          leads: leads,
          jobId: result.job_id,
          totalLeads: result.total_leads || 0
        });
      } else if (result.job_id) {
        addMessage('ai', result.ai_message);
        setCurrentJobId(result.job_id);

        // Track AI analysis state
        const _intent = result.intent || '';
        const _hasAI = result.has_ai_analysis || false;
        const _strategy = result.search_strategy || '';
        setSearchIntent(_intent);
        setHasAIAnalysis(_hasAI);
        setSearchStrategy(_strategy);

        // Show AI strategy card for multi-category / find_clients searches
        if (_strategy && (_intent === 'find_clients' || _intent === 'multi_category' || _intent === 'find_by_quality' || _intent === 'find_by_exclusion')) {
          const categories = result.target_categories || [];
          const cities = result.cities_searched || [];
          addMessage('ai', null, {
            type: 'ai_strategy',
            intent: _intent,
            strategy: _strategy,
            categories,
            cities,
            analysisMode: result.analysis_mode || 'quick',
          });
        }

        pollForResults(result.job_id, result.parsed_data?.count || 20);
      } else {
        addMessage('ai', result.ai_message || "I'm having trouble processing that right now.");
      }
    } catch (error) {
      if (error.message && (error.message.includes("Daily limit reached") || error.message.includes("403"))) {
        setUpgradeMessage("You've reached your daily lead limit. Upgrade your plan to get more leads!");
        setShowUpgradeModal(true);
      } else {
        addMessage('ai', "Oops! Something went wrong: " + error.message + ". Please try again.");
      }
    } finally {
      setIsLoading(false);
      setTimeout(() => saveToSupabase(), 500);
    }
  };

  const handleViewFullResults = async (resultsData) => {
    if (onSearchStart) {
      onSearchStart(
        { business_type: 'AI Search', location: 'AI Search' },
        resultsData.jobId
      );
    }
    
    try {
      const freshResults = await getJobResults(resultsData.jobId, 0, 1000);
      if (onSearchComplete) {
        onSearchComplete({
          leads: freshResults.leads,
          total: freshResults.total || freshResults.leads.length,
          filtered: freshResults.filtered || freshResults.leads.length,
          statistics: freshResults.statistics || {
            with_phone: freshResults.leads.filter(l => l.phone).length,
            with_website: freshResults.leads.filter(l => l.website).length,
            with_email: freshResults.leads.filter(l => l.email).length,
            hot_leads: freshResults.statistics?.hot_leads || freshResults.leads.filter(l => l.lead_grade === 'hot').length,
            warm_leads: freshResults.statistics?.warm_leads || freshResults.leads.filter(l => l.lead_grade === 'warm').length,
            cold_leads: freshResults.statistics?.cold_leads || freshResults.leads.filter(l => l.lead_grade === 'cold').length,
            without_website: freshResults.leads.filter(l => !l.website).length
          }
        });
      }
    } catch (error) {
      console.error('Error fetching fresh results:', error);
      if (onSearchComplete) {
        onSearchComplete({
          leads: resultsData.leads,
          total: resultsData.totalLeads,
          filtered: resultsData.totalLeads,
          statistics: {
            with_phone: resultsData.leads.filter(l => l.phone).length,
            with_website: resultsData.leads.filter(l => l.website).length,
            with_email: resultsData.leads.filter(l => l.email).length,
            hot_leads: resultsData.statistics?.hot_leads || resultsData.leads.filter(l => l.lead_grade === 'hot').length,
            warm_leads: resultsData.statistics?.warm_leads || resultsData.leads.filter(l => l.lead_grade === 'warm').length,
            cold_leads: resultsData.statistics?.cold_leads || resultsData.leads.filter(l => l.lead_grade === 'cold').length,
            without_website: resultsData.leads.filter(l => !l.website).length
          }
        });
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleTextareaChange = (e) => {
    setInputValue(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 150)}px`;
  };

  const handleNewChat = () => {
    if (parentNewChat) parentNewChat();
    saveToSupabase();
    setMessages([]);
    setSessionId(null);
    setSessionTitle('New Search');
    setCurrentJobId(null);
    setIsSearching(false);
    setSearchIntent('');
    setHasAIAnalysis(false);
    setSearchStrategy('');
    if (pollingRef.current) clearTimeout(pollingRef.current);
    if (storageKey) localStorage.removeItem(storageKey);
  };

  const quickPrompts = [
    { icon: '🏥', title: 'Hospitals in Delhi NCR', sub: 'Healthcare lead generation' },
    { icon: '🌐', title: 'Restaurants without website', sub: 'Find web development clients' },
    { icon: '⭐', title: 'Top rated CAs in Mumbai', sub: 'High rating professional search' },
    { icon: '🏗️', title: 'Construction companies in Bangalore', sub: 'B2B industry leads' },
    { icon: '🎬', title: 'Find me video editing clients on YouTube', sub: 'YouTube channels that need editors' },
    { icon: '🎨', title: 'Find thumbnail designer clients', sub: 'Growing channels needing better thumbnails' }
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] overflow-hidden bg-zinc-950">
      
      {messages.length === 0 ? (
        isRestoring ? (
          <div className="flex-1 flex flex-col items-center justify-center px-4">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-400 text-lg">Restoring your conversation...</p>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-start pt-16 px-6 overflow-y-auto min-h-0">
            <img src="/logo-small.png" alt="LeadGen AI" className="w-16 h-16 rounded-2xl object-contain mx-auto mb-6 mt-4 ring-1 ring-white/10" />
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 text-center">What leads do you need?</h1>
          <p className="text-gray-400 mb-8 text-center text-base">Describe in any language — English, Hindi, Hinglish</p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-3xl w-full">
            {quickPrompts.map((pq, idx) => (
              <button 
                key={idx} 
                className="group text-left p-4 bg-zinc-900/50 rounded-2xl hover:bg-zinc-900 transition-all duration-200"
                onClick={() => handleSendMessage(pq.title)}
              >
                <div className="text-xl mb-2 text-gray-500 group-hover:text-blue-400 transition-colors">{pq.icon}</div>
                <p className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors">{pq.title}</p>
                <p className="text-xs text-gray-500 mt-1">{pq.sub}</p>
              </button>
            ))}
          </div>
        </div>
        )
      ) : (
        <div className="flex-1 overflow-y-auto min-h-0">
          {messages.map((message) => (
            <div key={message.id} className="py-5 px-4 hover:bg-white/[0.02] transition-colors">
              <div className="max-w-3xl mx-auto flex gap-3">
                {message.type === 'user' ? (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0 text-white text-sm font-bold ring-1 ring-white/10">
                    {(profile?.name || profile?.email || 'U').charAt(0).toUpperCase()}
                  </div>
                ) : (
                  <img src="/logo-small.png" alt="LeadGen AI" className="w-8 h-8 rounded-full object-contain flex-shrink-0" />
                )}
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm text-white">
                      {message.type === 'user' ? 'You' : 'LeadGen AI'}
                    </span>
                    <span className="text-xs text-gray-500">{formatTime(message.timestamp)}</span>
                  </div>
                  
                  {message.text && (
                    <div className="text-gray-200 text-sm leading-relaxed whitespace-pre-wrap">
                      {message.text}
                    </div>
                  )}

                  {/* ── AI Strategy Card ── */}
                  {message.data && message.data.type === 'ai_strategy' && (
                    <div className="mt-3 bg-zinc-900/80 rounded-2xl overflow-hidden backdrop-blur-sm">
                      <div className="px-4 py-3 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-b border-white/5">
                        <div className="flex items-center gap-2">
                          <span className="text-base">🧠</span>
                          <span className="text-blue-400 font-semibold text-sm">AI Search Strategy</span>
                        </div>
                      </div>
                      <div className="px-4 py-3">
                        <p className="text-gray-300 text-sm leading-relaxed mb-3">{message.data.strategy}</p>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            message.data.intent === 'find_clients' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' :
                            message.data.intent === 'find_by_quality' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                            'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                          }`}>
                            🎯 {message.data.intent.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          </span>
                          {message.data.categories && message.data.categories.length > 1 && (
                            <span className="text-[10px] font-medium bg-zinc-800/50 text-gray-300 px-2 py-0.5 rounded-full">
                              📂 {message.data.categories.slice(0, 4).join(', ')}{message.data.categories.length > 4 ? ` +${message.data.categories.length - 4}` : ''}
                            </span>
                          )}
                          {message.data.cities && message.data.cities.length > 1 && (
                            <span className="text-[10px] font-medium bg-zinc-800/50 text-gray-300 px-2 py-0.5 rounded-full">
                              🏙️ {message.data.cities.slice(0, 4).join(', ')}{message.data.cities.length > 4 ? ` +${message.data.cities.length - 4}` : ''}
                            </span>
                          )}
                          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                            message.data.analysisMode === 'deep' ? 'bg-green-500/15 text-green-400' : 'bg-zinc-800/50 text-gray-400'
                          }`}>
                            {message.data.analysisMode === 'deep' ? '🤖 Deep AI Analysis' : '⚡ Quick Score'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {message.data && message.data.type === 'results' && (
                    <div className={`mt-4 bg-zinc-900/80 rounded-2xl overflow-hidden backdrop-blur-sm`}>

                      {/* AI-Analyzed banner */}
                      {(() => {
                        const leadsHaveAI = message.data.leads && message.data.leads.some(l => (l.ai_prospect_score || 0) > 0);
                        return leadsHaveAI ? (
                          <div className="px-4 py-2 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-b border-white/5">
                            <span className="text-[11px] font-medium text-blue-300">🤖 AI-Analyzed Results — Sorted by prospect potential</span>
                          </div>
                        ) : null;
                      })()}

                      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                        <div className="flex items-center gap-2">
                          {message.data.source === 'youtube' ? (
                            <>
                              <span className="text-base">🎬</span>
                              <span className="text-red-400 font-semibold text-sm">YouTube Leads Found — {message.data.totalLeads} channels</span>
                            </>
                          ) : message.data.source === 'platform' ? (
                            <>
                              <span className="text-base">🌐</span>
                              <span className="text-purple-400 font-semibold text-sm">Platform Leads Found — {message.data.totalLeads} leads from {(message.data.platforms || []).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' + ')}</span>
                            </>
                          ) : (
                            <>
                              <div className="w-2 h-2 rounded-full bg-green-500"></div>
                              <span className="text-green-400 font-semibold text-sm">Found {message.data.totalLeads} leads</span>
                            </>
                          )}
                        </div>
                        {message.data.source === 'youtube' ? (
                          <span className="text-[10px] font-bold uppercase tracking-wider bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full">YouTube</span>
                        ) : message.data.source === 'platform' ? (
                          <span className="text-[10px] font-bold uppercase tracking-wider bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">{(message.data.platforms || []).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' + ')}</span>
                        ) : null}
                      </div>
                      
                      {/* Prospect stats bar */}
                      {(() => {
                        const leads = message.data.leads || [];
                        const hotCount = leads.filter(l => l.ai_opportunity === 'hot' || l.lead_grade === 'hot').length;
                        const warmCount = leads.filter(l => l.ai_opportunity === 'warm' || l.lead_grade === 'warm').length;
                        const coldCount = leads.filter(l => l.ai_opportunity === 'cold' || l.lead_grade === 'cold').length;
                        return (hotCount > 0 || warmCount > 0 || coldCount > 0) ? (
                          <div className="flex items-center gap-3 px-4 py-1.5 border-t border-white/5">
                            {hotCount > 0 && <span className="text-[10px] font-medium bg-red-500/15 text-red-400 px-2 py-0.5 rounded-full">🔥 {hotCount} Hot</span>}
                            {warmCount > 0 && <span className="text-[10px] font-medium bg-yellow-500/15 text-yellow-400 px-2 py-0.5 rounded-full">🟡 {warmCount} Warm</span>}
                            {coldCount > 0 && <span className="text-[10px] font-medium bg-blue-500/15 text-blue-400 px-2 py-0.5 rounded-full">🔵 {coldCount} Cold</span>}
                          </div>
                        ) : null;
                      })()}
                      
                      <div className="divide-y divide-white/5">
                        {message.data.leads.slice(0, 3).map((lead, idx) => {
                          const aiScore = lead.ai_prospect_score || 0;
                          const aiOpp = lead.ai_opportunity || lead.lead_grade || '';
                          const aiWhy = lead.ai_why_they_need || '';
                          const showAI = aiScore > 0;

                          return (
                            <div key={idx} className="px-4 py-2 flex items-center justify-between relative">
                              <div className="flex flex-col min-w-0 pr-4">
                                <div className="flex items-center gap-1">
                                  <span className="text-sm text-white truncate flex-shrink min-w-0">{lead.name}</span>
                                  {/* AI opportunity badge (preferred) or fallback to lead_grade */}
                                  {showAI ? (
                                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ml-2 whitespace-nowrap border ${
                                      aiOpp === 'hot' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                                      aiOpp === 'warm' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                                      'bg-blue-500/20 text-blue-400 border-blue-500/30'
                                    }`}>
                                      {aiOpp === 'hot' ? '🔥 Hot Prospect' : aiOpp === 'warm' ? '🟡 Warm Prospect' : '🔵 Low Priority'}
                                    </span>
                                  ) : lead.lead_grade ? (
                                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ml-2 whitespace-nowrap ${
                                      lead.lead_grade === 'hot' ? 'bg-red-500/20 text-red-400' :
                                      lead.lead_grade === 'warm' ? 'bg-yellow-500/20 text-yellow-400' :
                                      'bg-blue-500/20 text-blue-400'
                                    }`}>
                                      {lead.lead_grade === 'hot' ? '🔥 Hot' : lead.lead_grade === 'warm' ? '🟡 Warm' : '🔵 Cold'}
                                    </span>
                                  ) : null}
                                </div>
                                {/* AI why-they-need insight */}
                                {showAI && aiWhy && aiWhy !== 'Analysis pending' && (
                                  <span className="text-xs text-gray-400 italic mt-0.5 truncate max-w-[280px]">
                                    {aiWhy.length > 60 ? aiWhy.substring(0, 60) + '...' : aiWhy}
                                  </span>
                                )}
                                {message.data.source === 'platform' && lead.headline && (
                                  <span className="text-xs text-gray-400 truncate max-w-[200px]">{lead.headline.length > 40 ? lead.headline.substring(0, 40) + '...' : lead.headline}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 shrink-0">
                                {/* AI score circle */}
                                {showAI && (
                                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border ${
                                    aiScore >= 70 ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                                    aiScore >= 40 ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                                    'bg-blue-500/20 text-blue-400 border-blue-500/30'
                                  }`}>
                                    {aiScore}
                                  </span>
                                )}
                                {lead.phone && <span className="text-xs text-blue-400">{lead.phone}</span>}
                                {message.data.source === 'youtube' && lead.total_ratings > 0 && (
                                  <span className="text-xs text-red-400">{lead.total_ratings.toLocaleString()} subs</span>
                                )}
                                {message.data.source === 'platform' && lead.platform && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                    lead.platform.toLowerCase() === 'linkedin' ? 'bg-blue-400/20 text-blue-400' :
                                    lead.platform.toLowerCase() === 'instagram' ? 'bg-pink-400/20 text-pink-400' :
                                    lead.platform.toLowerCase() === 'facebook' ? 'bg-blue-500/20 text-blue-500' :
                                    lead.platform.toLowerCase() === 'twitter' || lead.platform.toLowerCase() === 'x' ? 'bg-gray-300/20 text-gray-300' :
                                    lead.platform.toLowerCase() === 'justdial' ? 'bg-yellow-400/20 text-yellow-400' :
                                    lead.platform.toLowerCase() === 'indiamart' ? 'bg-blue-600/20 text-blue-600' :
                                    'bg-slate-600/30 text-white'
                                  }`}>{lead.platform.charAt(0).toUpperCase() + lead.platform.slice(1)}</span>
                                )}
                                {message.data.source !== 'youtube' && message.data.source !== 'platform' && lead.rating > 0 && <span className="text-xs text-yellow-400 flex items-center gap-1"><span>★</span> {lead.rating}</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {message.data.totalLeads > 3 && (
                        <div className="px-4 py-1.5 text-center text-xs text-gray-500">
                          ...and {message.data.totalLeads - 3} more {message.data.source === 'youtube' ? 'channels' : 'leads'}
                        </div>
                      )}

                      {(message.data.source === 'youtube' || message.data.source === 'platform') && (
                        <div className="px-4 py-1.5 text-center text-[11px] text-gray-500 border-t border-white/5">
                          Source: {message.data.source === 'youtube' ? 'YouTube Channels' : (message.data.platforms || []).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(', ')}
                        </div>
                      )}

                      <div className="px-4 py-3 border-t border-white/5">
                        <button 
                          className={`w-full py-2.5 text-white font-semibold rounded-xl hover:shadow-lg transition-all text-sm ${
                            message.data.source === 'youtube' ? 'bg-gradient-to-r from-red-500 to-rose-600 hover:shadow-red-500/25' : 
                            message.data.source === 'platform' ? 'bg-gradient-to-r from-purple-500 to-indigo-600 hover:shadow-purple-500/25' : 
                            'bg-gradient-to-r from-blue-500 to-purple-600 hover:shadow-blue-500/25'
                          }`}
                          onClick={() => handleViewFullResults(message.data)}
                        >
                          View Full Results →
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="py-5 px-4">
              <div className="max-w-3xl mx-auto flex gap-3">
                <img src="/logo-small.png" alt="LeadGen AI" className="w-8 h-8 rounded-full object-contain flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm text-white">LeadGen AI</span>
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {isSearching && (
            <div className="py-5 px-4">
              <div className="max-w-3xl mx-auto flex gap-3">
                <img src="/logo-small.png" alt="LeadGen AI" className="w-8 h-8 rounded-full object-contain flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm text-white">LeadGen AI</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-gray-400 text-sm">Searching for leads...</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      )}

      {/* INPUT AREA */}
      <div className="flex-shrink-0 bg-zinc-950 px-4 py-4">
        <div className="max-w-3xl mx-auto">
          <div className="relative flex items-end bg-zinc-900 rounded-[20px] px-4 py-3 focus-within:ring-2 focus-within:ring-blue-500/30 transition-all shadow-lg shadow-black/20">
            {messages.length > 0 && (
              <button
                onClick={handleNewChat}
                className="mr-3 flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-zinc-800 hover:bg-zinc-700 text-gray-400 hover:text-white transition-colors"
                title="New Chat"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            )}
            <textarea
              ref={inputRef}
              className="flex-1 bg-transparent text-white placeholder-gray-500 resize-none focus:outline-none text-[15px] leading-normal max-h-[150px] min-h-[24px]"
              rows={1}
              placeholder="Describe what leads you need..."
              value={inputValue}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              disabled={isLoading || isSearching}
            />
            <button
              onClick={() => handleSendMessage()}
              disabled={!inputValue.trim() || isLoading || isSearching}
              className={`ml-2 flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
                inputValue.trim() && !isLoading && !isSearching 
                  ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-md shadow-blue-500/25' 
                  : 'bg-zinc-800 text-gray-600 cursor-not-allowed'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 19V5m0 0l-7 7m7-7l7 7" />
              </svg>
            </button>
          </div>
          <p className="text-center text-xs text-gray-600 mt-2">
            LeadGen AI can search businesses worldwide. Works in English, Hindi & Hinglish.
          </p>
        </div>
      </div>

      {showUpgradeModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowUpgradeModal(false)}>
          <div className="bg-zinc-900 rounded-2xl p-8 max-w-md w-full mx-auto text-center" onClick={(e) => e.stopPropagation()}>
            <div className="text-5xl mb-4">🚀</div>
            <h2 className="text-white text-2xl font-bold mb-4">Upgrade Your Plan</h2>
            <p className="text-gray-300 mb-8">{upgradeMessage}</p>
            <div className="flex gap-4 justify-center items-center">
              <button 
                onClick={() => setShowUpgradeModal(false)} 
                className="text-gray-400 hover:text-white px-4 py-2 font-medium transition-colors"
              >
                Maybe Later
              </button>
              <Link 
                to="/pricing" 
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
                onClick={() => setShowUpgradeModal(false)}
              >
                View Plans
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatSearch;
